import { AlertTriangle, Clock, Flame, X } from 'lucide-react'
import type { PromoAlert } from '@/types'
import { formatCurrency } from '@/lib/formatters'
import { PROMO_ALERT_COLORS } from '@/lib/constants'
import { useNotificationStore } from '@/store'

interface PromoAlertBannerProps {
  alerts: PromoAlert[]
}

export function PromoAlertBanner({ alerts }: PromoAlertBannerProps) {
  const dismiss = useNotificationStore((s) => s.dismiss)

  const mostUrgent = alerts[0]
  if (alerts.length === 0 || !mostUrgent) return null

  const isExpired = mostUrgent.severity === 'expired'
  const visible = alerts.slice(0, 3)
  const hidden = alerts.length - visible.length

  return (
    <div
      className={[
        'border-b px-4 py-2',
        isExpired ? 'bg-red-950/60 border-red-700' : 'bg-orange-950/40 border-orange-700/60',
      ].join(' ')}
    >
      <div className="max-w-screen-2xl mx-auto flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 shrink-0">
          {isExpired ? (
            <Flame className="w-4 h-4 text-red-400" />
          ) : (
            <AlertTriangle className="w-4 h-4 text-orange-400" />
          )}
          <span
            className={[
              'text-sm font-semibold',
              isExpired ? 'text-red-300' : 'text-orange-300',
            ].join(' ')}
          >
            {isExpired ? 'PROMO EXPIRED' : 'PROMO EXPIRING SOON'}
          </span>
        </div>

        <div className="flex items-center gap-2 flex-wrap text-sm">
          {visible.map((alert) => (
            <div
              key={alert.id}
              className={[
                'group flex items-center gap-2 px-2 py-1 rounded border text-xs',
                PROMO_ALERT_COLORS[alert.severity],
              ].join(' ')}
            >
              <Clock className="w-3 h-3 shrink-0" />
              <span className="font-medium">{alert.debtName}</span>
              <span className="opacity-75">
                {alert.severity === 'expired' ? 'EXPIRED' : `${Math.ceil(alert.monthsRemaining)}mo`}
              </span>
              <span>
                {alert.currentAPR}% → <span className="font-bold">{alert.postPromoAPR}%</span>
              </span>
              <span className="opacity-75">
                +{formatCurrency(alert.estimatedMonthlyInterestIncrease)}/mo
              </span>
              <button
                onClick={() => dismiss(alert.id)}
                title="Dismiss this alert"
                className="ml-1 -mr-1 p-0.5 rounded hover:bg-white/10 opacity-60 hover:opacity-100 transition-opacity"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
          {hidden > 0 && (
            <span className="text-xs text-slate-400">+{hidden} more (see bell icon)</span>
          )}
        </div>
      </div>
    </div>
  )
}
